import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LockSimpleIcon } from '@phosphor-icons/react';
import type { Resource } from '@growhero/shared';
import { useTenant } from '@/tenant/TenantProvider';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';
import { Skeleton, Tooltip } from '@/ui';

/**
 * Bars follow the product-area colours (messages = sea-teal, AI = light blue). The meters live in
 * the sidebar (brand field), so surface-field swaps in the light field variants; a low balance turns saffron, the A3
 * "needs attention" colour (red would not read on the blue).
 */
const ORDER: { r: Resource; bar: string }[] = [
  { r: 'sms', bar: 'bg-area-messages' },
  { r: 'email', bar: 'bg-area-messages' },
  { r: 'ai', bar: 'bg-area-ai' },
];

/** Compact SMS / Email / AI balances. Denominator = this month's plan allowance (or balance if higher). */
export function CreditMeters({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation('shell');
  const { current, wallet, hasFeature } = useTenant();
  if (!current) return <Skeleton className="h-24 w-full rounded-lg" />;
  return (
    <div className="rounded-lg bg-field-raised px-3 pb-3 pt-2.5 shadow-[inset_0_0_0_1px_var(--gh-field-line)]">
      <div className="mb-2 flex items-center justify-between">
        <span className="eyebrow text-[0.625rem] text-on-field-subtle">{t('credits.title')}</span>
        <Link
          to="/billing"
          onClick={onNavigate}
          className="rounded-[3px] text-xs font-bold text-field-accent-text underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-field-focus"
        >
          {t('credits.topUp')}
        </Link>
      </div>
      <div className="grid gap-2">
        {ORDER.map(({ r, bar }) => {
          const w = wallet(r);
          const allowance = current.plan?.allowances[r] ?? 0;
          const available = w?.available ?? 0;
          const max = Math.max(available, allowance, 1);
          const ratio = Math.min(1, available / max);
          const locked = r === 'ai' && !hasFeature('ai.assistant');
          const low = !locked && allowance > 0 && available / allowance <= 0.15;
          return (
            <Tooltip
              key={r}
              side="right"
              content={
                locked
                  ? t('credits.aiLocked')
                  : t('credits.tooltip', {
                      available: formatNumber(available),
                      reserved: formatNumber(w?.reserved ?? 0),
                    })
              }
            >
              <div
                className={cn(
                  'grid gap-1 rounded-[3px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-field-focus',
                  locked && 'opacity-70',
                )}
                tabIndex={0}
                aria-label={`${t(`credits.${r}`)}: ${formatNumber(available)}`}
              >
                <div className="flex items-baseline justify-between text-xs">
                  <span className="font-semibold text-on-field-muted">{t(`credits.${r}`)}</span>
                  <span
                    className={cn(
                      'num font-extrabold',
                      low ? 'text-field-accent-text' : 'text-on-field',
                    )}
                  >
                    {locked ? (
                      <LockSimpleIcon
                        weight="bold"
                        className="inline size-3 text-on-field-subtle"
                        aria-hidden
                      />
                    ) : (
                      formatNumber(available)
                    )}
                    {!locked && allowance > 0 && available <= allowance && (
                      <span className="font-medium text-on-field-subtle">
                        {' '}
                        / {formatNumber(allowance)}
                      </span>
                    )}
                  </span>
                </div>
                <div className="h-1 overflow-hidden rounded-full bg-white/12">
                  <div
                    className={cn(
                      'h-full rounded-full transition-[width] duration-700',
                      low ? 'bg-field-accent' : bar,
                    )}
                    style={{ width: `${locked ? 0 : ratio * 100}%` }}
                  />
                </div>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
